import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { annonceActive, fenetreEnCours, prochaineFenetre, libelleFenetre, titreAnnonce } from "../lib/dates";
import { useReglages } from "../lib/useReglages";

/**
 * Bandeau d'annonce en haut de toutes les pages.
 * S'allume trois semaines avant les portes ouvertes saisies dans le classeur de l'école,
 * s'éteint le lendemain de leur dernier jour, et reste fermé si le visiteur l'a fermé.
 * Aucune date n'est écrite ici : tout vient de `GET /api/reglages`.
 */
export default function AnnouncementBar() {
  const { portes_ouvertes: fenetres } = useReglages();
  const [ferme, setFerme] = useState(false);
  const ref = React.useRef(null);

  const fenetre = prochaineFenetre(fenetres);
  const enCours = !!fenetreEnCours(fenetres);
  // Chaque nouvelle fenêtre est une nouvelle annonce : la clé change avec sa date de début,
  // donc le bandeau se réaffiche même à ceux qui avaient fermé le précédent.
  const cleFermeture = fenetre ? `1000arts-annonce-po-${fenetre.debut}` : null;
  const visible = annonceActive(fenetres) && !ferme;

  useEffect(() => {
    if (!cleFermeture) return;
    try {
      setFerme(window.localStorage.getItem(cleFermeture) === "1");
    } catch (e) {
      /* navigation privée : on affiche */
    }
  }, [cleFermeture]);

  // Le menu est en position fixe : on publie la hauteur du bandeau dans une variable CSS
  // que le menu et le contenu utilisent pour se décaler. Recalculée au redimensionnement
  // car le texte passe sur deux lignes sur mobile.
  useEffect(() => {
    const racine = document.documentElement;
    if (!visible) {
      racine.style.setProperty("--annonce-h", "0px");
      return undefined;
    }
    const mesurer = () => {
      if (ref.current) racine.style.setProperty("--annonce-h", `${ref.current.offsetHeight}px`);
    };
    mesurer();
    window.addEventListener("resize", mesurer);
    return () => {
      window.removeEventListener("resize", mesurer);
      racine.style.setProperty("--annonce-h", "0px");
    };
  }, [visible]);

  const fermer = () => {
    setFerme(true);
    try { if (cleFermeture) window.localStorage.setItem(cleFermeture, "1"); } catch (e) { /* ignoré */ }
  };

  if (!visible) return null;

  return (
    <div
      ref={ref}
      data-testid="announcement-bar"
      className="fixed top-0 left-0 right-0 z-[60] text-white overflow-hidden"
      style={{ background: "linear-gradient(90deg, #0B2A4A 0%, var(--primary) 45%, #0E9AA7 100%)" }}
    >
      {/* Reflet qui traverse lentement la bande : attire l'œil sans clignoter. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 w-1/3 annonce-reflet"
        style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)" }}
      />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-14 py-3 pr-12 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">
        <span className="inline-flex items-center gap-2 text-[15px] md:text-base font-bold tracking-tight">
          <span
            className="w-2.5 h-2.5 rounded-full animate-pulse shrink-0"
            style={{ background: "#FDE68A", boxShadow: "0 0 0 4px rgba(253,230,138,0.25)" }}
            aria-hidden="true"
          />
          {enCours ? "Portes ouvertes — cours d'essai offerts" : titreAnnonce()}
        </span>
        <span className="hidden md:inline opacity-30" aria-hidden="true">|</span>
        <span className="text-sm md:text-[15px] opacity-95">
          Portes ouvertes {libelleFenetre(fenetre)} —{" "}
          <strong className="font-semibold" style={{ color: "#FDE68A" }}>cours d'essai offerts</strong>
        </span>
        <Link
          to="/inscription"
          data-testid="announcement-bar-cta"
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold text-[#0B2A4A] transition-transform hover:scale-105 active:scale-95"
          style={{ background: "#FDE68A", boxShadow: "0 2px 10px rgba(0,0,0,0.18)" }}
        >
          Réserver ma place <ArrowRight size={15} />
        </Link>
      </div>
      <button
        type="button"
        onClick={fermer}
        aria-label="Fermer l'annonce"
        data-testid="announcement-bar-close"
        className="absolute top-1/2 right-3 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
      >
        <X size={15} />
      </button>
    </div>
  );
}
